import type { RefObject } from 'react';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useShiftRangeMultiSelect } from '@src/hooks/useShiftRangeMultiSelect';
import FJLocalStore from '../../store/FJLocalStore';
import FJScroller from '../../common/components/FJScroller/FJScroller';
import type { FJDetailsListItem } from '../../redux/action/FJDetailsPageAction';
import FJDetailsCard from './FJDetailsCard';
import FJDetailsSidebar from './FJDetailsSidebar';
import styles from './FJDetailsPage.module.less';
import FJBlankPanel from '@src/common/components/FJBlankPanel/FJBlankPanel';
import { FJDetailsPlaybackContext, FJDetailsPlaybackProvider } from './FJDetailsPlaybackContext';
import FJSelectAll from '@src/common/components/FJSelectAll/FJSelectAll';

/** 侧栏/列表切换当前片段时清空独占播放，避免多卡片同时处于「正在播放」状态 */
function DetailsPlaybackSelectionSync({ selectedId }: { selectedId: string | null }) {
    const playback = useContext(FJDetailsPlaybackContext);
    const clearPlayback = playback?.clearPlayback;
    useEffect(() => {
        clearPlayback?.();
    }, [selectedId, clearPlayback]);
    return null;
}

/** 进入多选时暂停当前预览（不在退出多选时暂停：播放会先 exit 多选，若此时 pause 会清空 playingId 导致再次多选无法停播） */
function DetailsMultiSelectPlaybackSync({ selectedClipIds }: { selectedClipIds: string[] }) {
    const playback = useContext(FJDetailsPlaybackContext);
    const prevCountRef = useRef(0);

    useEffect(() => {
        const prev = prevCountRef.current;
        const next = selectedClipIds.length;
        if (prev === 0 && next > 0) {
            playback?.pauseActivePlayback();
        }
        prevCountRef.current = next;
    }, [selectedClipIds, playback]);

    return null;
}

interface FJDetailsPageProps {
    items: FJDetailsListItem[];
    selectedId: string | null;
    selectedClipIds: string[];
    mainRef: RefObject<HTMLDivElement | null>;
    onSelectItem: (id: string) => void;
    onBack: () => void;
    onToggleLike: (id: string) => void;
    onToggleDislike: (id: string) => void;
    onTryAgain?: (id: string) => void;
    sidebarProjectTitle?: string;
    /** 工具条「已发布」角标 */
    onPublished?: (clipId: string) => void;
    /** 工具条「发布到社交平台」 */
    onPublishSocial?: (clipId: string) => void;
    onExportDownload?: (clipId: string, callback: () => void) => void;
    onTrim?: (clipId: string) => void;
    onCut?: (clipId: string, callback: () => void) => void;
    onMoreRename?: (clipId: string) => void;
    onMoreDelete?: (clipId: string) => void;
    onSetClipSelection: (ids: Array<string | number>) => void;
    onToggleClipSelection: (id: string) => void;
    onMultiDelete: (ids: Array<string | number>) => void;
    onClearClipSelection: () => void;
}

export default function FJDetailsPage({
    items,
    selectedId,
    selectedClipIds,
    mainRef,
    onSelectItem,
    onBack,
    onToggleLike,
    onToggleDislike,
    onTryAgain,
    sidebarProjectTitle,
    onPublished,
    onPublishSocial,
    onExportDownload,
    onTrim,
    onCut,
    onMoreRename,
    onMoreDelete,
    onSetClipSelection,
    onToggleClipSelection,
    onMultiDelete,
    onClearClipSelection,
}: FJDetailsPageProps) {
    const handleSetClipSelection = useCallback(
        (ids: string[]) => onSetClipSelection(ids),
        [onSetClipSelection],
    );

    const { handleItemSelectClick } = useShiftRangeMultiSelect({
        items,
        selectedIds: selectedClipIds,
        onSetSelection: handleSetClipSelection,
        onToggleOne: onToggleClipSelection,
    });

    const onCardToggleClipSelected = useCallback(
        (id: string, shiftKey?: boolean) => {
            handleItemSelectClick(id, shiftKey);
        },
        [handleItemSelectClick],
    );

    return (
        <FJDetailsPlaybackProvider>
            <DetailsPlaybackSelectionSync selectedId={selectedId} />
            <DetailsMultiSelectPlaybackSync selectedClipIds={selectedClipIds} />
            <div className={styles.root}>
                <FJDetailsSidebar
                    items={items}
                    selectedId={selectedId}
                    onSelect={onSelectItem}
                    onBack={onBack}
                    projectTitle={sidebarProjectTitle}
                />
                <FJScroller className={styles.main} ref={mainRef}>
                    {items.length === 0 && <FJBlankPanel text={FJLocalStore._('all-projects-have-been-deleted')} />}
                    {items.map((item, index) => (
                        <FJDetailsCard
                            key={item.id}
                            item={item}
                            ordinal={index + 1}
                            isSelected={item.id === selectedId}
                            isClipSelected={selectedClipIds.some(sid => String(sid) === String(item.id))}
                            onToggleClipSelected={onCardToggleClipSelected}
                            onToggleLike={onToggleLike}
                            onToggleDislike={onToggleDislike}
                            onTryAgain={onTryAgain}
                            onPublished={onPublished}
                            onPublishSocial={onPublishSocial}
                            onExportDownload={onExportDownload}
                            onTrim={onTrim}
                            onCut={onCut}
                            onMoreRename={onMoreRename}
                            onMoreDelete={onMoreDelete}
                            onClearClipSelection={onClearClipSelection}
                        />
                    ))}
                </FJScroller>
            </div>
            <FJSelectAll
                list={items}
                selectList={selectedClipIds}
                setSelectList={onSetClipSelection}
                multiDelete={onMultiDelete}
                clearSelected={onClearClipSelection}
            />
        </FJDetailsPlaybackProvider>
    );
}
